import type { UnknownRecord } from '@bonsai-components/utility-types';
import {
  createContext,
  type Dispatch,
  type FunctionComponent,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { QueryExample } from '../components/graphql-explorer/see-how-to-run-graphql.component';

type ApiExamplesContextType = {
  examples: QueryExample<UnknownRecord>[];
  setExamples: Dispatch<SetStateAction<QueryExample<UnknownRecord>[]>>;
};

const ApiExamplesContext = createContext<ApiExamplesContextType | undefined>(
  undefined
);

type ApiExamplesProviderProps = {
  children: ReactNode;
};

export const ApiExamplesProvider: FunctionComponent<
  ApiExamplesProviderProps
> = ({ children }) => {
  const [examples, setExamples] = useState<QueryExample<UnknownRecord>[]>([]);

  const value = useMemo(() => ({ examples, setExamples }), [examples]);

  return (
    <ApiExamplesContext.Provider value={value}>
      {children}
    </ApiExamplesContext.Provider>
  );
};

export const useApiExamplesContext = (): ApiExamplesContextType => {
  const context = useContext(ApiExamplesContext);
  if (!context) {
    throw new Error(
      'useApiExamplesContext must be used within ApiExamplesProvider'
    );
  }
  return context;
};

/**
 * Hook for pages to register their API examples.
 */
export const useSetApiExamples = (
  examples: QueryExample<UnknownRecord>[]
): void => {
  const { setExamples } = useApiExamplesContext();
  // Use useEffect with JSON comparison to prevent updates when content hasn't changed
  // This prevents infinite re-render loops caused by new array references
  useEffect(() => {
    setExamples((prevExamples) => {
      // Only update if the content actually changed
      if (JSON.stringify(prevExamples) === JSON.stringify(examples)) {
        return prevExamples;
      }
      return examples;
    });
  }, [examples, setExamples]);

  // Clear examples on unmount so pages that don't call useSetApiExamples
  // (e.g., changelog) don't show stale GraphQL icons from a previous page.
  useEffect(() => {
    return () => {
      setExamples([]);
    };
  }, [setExamples]);
};
